import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { sortBy } from 'underscore';

import { useTranslate } from '../../../../i18n/useTranslationModules';
import { LanguagesAbbr, PageModule, Session, SessionPreview, SessionTypesEnum, SessionTypesFeatures } from '../../../../types/working-model';
import { getStylingOverrides } from '../../../live-event/utils';
import { filterSessions, SessionFilter, SessionFilterArgs } from './session-filter';
import { sessionsFromVodOrder } from '../../../../utils/utils';
import { OptionalComponent } from '../../../../utils/optional-component';
import { agendaCalendarViewEnum } from '../../../../types/template-layouts';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import AgendaCalendar from './agenda-calendar';
import Icon, { ICONS } from '../../../general-ui/icon';
import { CalendarBroadcast } from './calendar-view-broadcast';
import { CalendarOndemand } from './calendar-view-ondemand';

interface IGroupedSessions {
	heading: string;
	sessions: Array<Session | SessionPreview>;
}
interface CalendarLayoutProps {
	module: PageModule;
	template: string;
	sessions: Array<Session | SessionPreview>;
	language: LanguagesAbbr;
	editor?: boolean;
	isAgendaPage?: boolean;
	userSavedSessions?: string[]
}

export const CalendarLayout = ({
	module,
	template,
	sessions,
	language,
	editor = false,
	isAgendaPage = false,
	userSavedSessions,
}: CalendarLayoutProps): JSX.Element => {

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const [filter, setFilter] = useState<SessionFilterArgs>({ viewType: agendaCalendarViewEnum.LIST });
	const [previousDaySessions, setPreviousDaySessions] = useState<number>(0);
	const [displayPastSessions, setDisplayPastSessions] = useState(!isAgendaPage);

	const stylingOverrides = getStylingOverrides(module.styling_overrides);
	const { t } = useTranslate('homepage');

	const vodOrder = workingEvent ? workingEvent.settings.vodOrder : eventBundle?.settings.vodOrder;

	const filteredSessions = useMemo(() => {
		let filteredSessions = filterSessions({ sessions, filter });
		if (!workingEvent && !displayPastSessions && !filter.day) {
			const today = new Date().setHours(0, 0, 0, 0);
			let numberOfPastSessions = 0;
			let numberOfNonTimestampSessions = 0;

			const futureSessions = filteredSessions.filter(session => {
				const hasTimestamp = SessionTypesFeatures[session.session_type].has_start_time;
				if (!hasTimestamp) {
					// if a session doesnt have a start time then we will include it in the future session list.
					numberOfNonTimestampSessions++;
					return true;
				}
				if (!session?.timestamp) return false;

				const dayOfSession = new Date(session.timestamp).setHours(0, 0, 0, 0);
				const isTodayOrFuture = dayOfSession >= today;
				!isTodayOrFuture && numberOfPastSessions++;

				return isTodayOrFuture;
			});

			if (futureSessions.length !== numberOfNonTimestampSessions) {
				filteredSessions = futureSessions;
				setPreviousDaySessions(numberOfPastSessions);
			} else if (!futureSessions.length || numberOfNonTimestampSessions === futureSessions.length) {
				// if all future sessions are filtered out we need to reset previousDaySessions so the banner doesnt display
				setPreviousDaySessions(0);
			}
		}

		return filteredSessions;
	}, [sessions, filter, workingEvent, displayPastSessions]);

	const groupedSessions: IGroupedSessions[] = useMemo(() => {
		const sorted = sortBy(filteredSessions, 'timestamp');

		const scheduledSessions: IGroupedSessions[] = []; /* grouping sessions by day in an array in date order*/
		let currentDay: string; /* keeps track of current day as we iterate through sorted sessions */
		let currentIndex = -1; /* keeps track of current index for sessions to be added to liveSessions, starting at -1 so the first iteration will increment to 0 */
		const onDemandSessions: Array<Session | SessionPreview> = [];

		sorted.forEach(session => {
			if (session.session_type === SessionTypesEnum.onDemand || !session.timestamp) {
				onDemandSessions.push(session);
			} else {
				const date = new Date(session.timestamp).toDateString();
				// if this is the first date or it's different than the last, we need to create the current group and set our index counter
				if (date !== currentDay) {
					currentDay = date;
					scheduledSessions.push({ heading: date, sessions: [] });
					currentIndex++;
				}
				// push each session to the current group
				scheduledSessions[currentIndex].sessions.push(session);
			}
		});

		if (!onDemandSessions.length) {
			return scheduledSessions;
		}

		return [...scheduledSessions, { heading: 'On Demand', sessions: sessionsFromVodOrder(onDemandSessions, vodOrder) }];
	}, [filteredSessions, vodOrder]);

	const shouldDisplayBanner = !workingEvent && !displayPastSessions && !!previousDaySessions && !filter.day;

	return (
		<div className={classNames('agenda-sessions-wrapper', { 'list-view': filter.viewType === agendaCalendarViewEnum.LIST })}>
			<div className={classNames('agenda-sessions-header agenda-calendar-filters')}>
				<SessionFilter
					isLive={false}
					module={module}
					sessions={sessions}
					template={template}
					onFilterChange={setFilter}
					overrides={stylingOverrides.dropdownOverrides}
					userSavedSessions={isAgendaPage ? userSavedSessions : undefined}
					calendarViewFilters
					isAgendaPage={isAgendaPage}
				/>
			</div>

			<OptionalComponent display={shouldDisplayBanner}>
				<button
					className="evt-button secondary past-session-banner"
					onClick={() => setDisplayPastSessions(true)}
				>
					{t("Show")}{` ${previousDaySessions} `}{t("previous sessions")}
				</button>
			</OptionalComponent>

			<OptionalComponent display={filter?.viewType === agendaCalendarViewEnum.LIST}>
				<div className={'agenda-sessions-list'}>
					{groupedSessions.map(sessionGroup => (
						<div key={sessionGroup.heading} className={'agenda-sessions-section'}>
							<OptionalComponent display={sessionGroup.heading !== "On Demand"}>
								<CalendarBroadcast
									sessionGroup={sessionGroup}
									module={module}
									language={language}
									template={template}
									editor={editor}
									isAgendaPage={isAgendaPage}
								/>
							</OptionalComponent>

							<OptionalComponent display={sessionGroup.heading === "On Demand"}>
								<CalendarOndemand
									isAgendaPage={isAgendaPage}
									sessionGroup={sessionGroup}
									module={module}
									language={language}
									template={template}
									editor={editor}
									filter={filter}
								/>
							</OptionalComponent>
						</div>
					))}
				</div>
			</OptionalComponent>
			<OptionalComponent display={filter?.viewType === agendaCalendarViewEnum.CALENDAR}>
				<AgendaCalendar sessions={filteredSessions} />
			</OptionalComponent>
		</div>
	);
};
